import { Link } from "react-router-dom";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import {
  useAddBudgetLineFreelancer,
  useRemoveBudgetLineFreelancer,
  type BudgetLineFreelancerRow,
} from "@/hooks/use-budget-line-freelancers";
import { FreelancerPicker } from "./FreelancerPicker";

interface Props {
  budgetLineId: string;
  lineName: string;
  projectId: string;
  links: BudgetLineFreelancerRow[];
  canEdit: boolean;
}

/**
 * VIB-2927: frilanserne på en ekstern kostnadslinje, under linjenavnet i
 * Timetildeling. Dagsprisen er informasjon; estimert kost endres bare i tilbudet.
 */
export function BudgetLineFreelancers({ budgetLineId, lineName, projectId, links, canEdit }: Props) {
  const add = useAddBudgetLineFreelancer();
  const remove = useRemoveBudgetLineFreelancer();
  if (links.length === 0 && !canEdit) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-[1.375rem]">
      {links.map((l) => (
        <span key={l.freelancer_id} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
          <SrIcon name="film" className="h-3 w-3 text-muted-foreground" />
          <Link to={`/frilansere/${l.freelancer_id}`} className="hover:underline">{l.freelancer?.name ?? "Frilanser"}</Link>
          {l.freelancer?.day_rate_nok != null && (
            <span className="text-muted-foreground">· {formatNOK(l.freelancer.day_rate_nok)}/dag</span>
          )}
          {canEdit && (
            <button
              type="button"
              aria-label={`Fjern ${l.freelancer?.name ?? "frilanser"} fra ${lineName}`}
              className="text-muted-foreground hover:text-destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate({ budgetLineId, freelancerId: l.freelancer_id, projectId })}
            >
              <SrIcon name="x" className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <FreelancerPicker
          context={lineName}
          excludeIds={links.map((l) => l.freelancer_id)}
          onPick={(freelancerId) => add.mutate({ budgetLineId, freelancerId, projectId })}
          trigger={
            <button
              type="button"
              aria-label={`Velg frilanser på ${lineName}`}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground"
            >
              <SrIcon name="plus" className="h-3 w-3" />
              Frilanser
            </button>
          }
        />
      )}
    </div>
  );
}
